import { Button } from '@cian/ui-kit';
import { TRoundButtonSize } from '@cian/ui-kit/button/types/TButtonSize';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getButtonText } from 'shared/containers/AsideContacts/utils/getButtonText';
import { isNewbuildingFromDeveloperSelector } from 'shared/selectors/offerData/offer/isNewbuildingFromDeveloperSelector';

import { getAgentAvailability, getPhones, getVillageId, selectOffer, selectUser } from '../../selectors';
import { getDynamicCalltrackingSiteBlockId } from '../../selectors/getDynamicCalltrackingSiteBlockId';
import { isRenterFormEnabled } from '../../selectors/isRenterFormEnabled';
import { getRosreestrInfo } from '../../selectors/offerData/getRosreestrInfo';
import { selectAgent } from '../../selectors/selectAgent';
import { prefetchFormState } from '../../store/deal-rent';
import { setPhoneCollapse } from '../../store/phone_collapse';
import { SimpleBotDetector } from '../../utils/simpleBotDetector';
import { useApplicationContext } from '../ApplicationContext';
import { PhonesOpened } from './PhonesOpened';
import { trackShowPhoneClick } from './tracking';

interface IPhonesContainerProps {
  size?: TRoundButtonSize;
  trackingAction?: string;
}

export const PhonesContainer: React.FC<IPhonesContainerProps> = ({ size = 'M', trackingAction }) => {
  const [isPhonesOpened, setPhonesOpened] = React.useState(false);
  const dispatch = useDispatch();
  const { logger } = useApplicationContext();
  const offer = useSelector(selectOffer);
  const phones = useSelector(getPhones);
  const isDealRentFormEnabled = useSelector(isRenterFormEnabled);
  const agent = useSelector(selectAgent);
  const agentAvailability = useSelector(getAgentAvailability);
  const user = useSelector(selectUser);
  const siteBlockId = useSelector(getDynamicCalltrackingSiteBlockId);
  const rosreestrInfo = useSelector(getRosreestrInfo);
  const villageId = useSelector(getVillageId);
  const isNewbuildingFromBuilder = useSelector(isNewbuildingFromDeveloperSelector);
  const buttonContent = getButtonText({ isDealRent: isDealRentFormEnabled, isNewbuildingFromBuilder });

  const onShowPhoneClick = () => {
    if (isDealRentFormEnabled) {
      dispatch(prefetchFormState());

      return;
    }

    setPhonesOpened(true);
    dispatch(setPhoneCollapse());
  };

  const trackShowPhone = () => {
    const botDetector = new SimpleBotDetector();
    const isAgentNotAvailable = agentAvailability && !agentAvailability.available;

    trackShowPhoneClick({
      trackingAction,
      offer,
      agent,
      user,
      kpId: villageId,
      bot: !botDetector.isHuman(),
      isAgentNotAvailable,
      siteBlockId,
      rosreestrInfo,
    });
  };

  const handleButtonClick: VoidFunction = () => {
    trackShowPhone();
    onShowPhoneClick();
  };

  if (!phones.length || phones.some(phone => !phone.countryCode || !phone.number)) {
    logger.error(`Offer #${offer.cianId}: phones is null`);

    return null;
  }

  return (
    <div>
      {isPhonesOpened && <PhonesOpened />}
      {!isPhonesOpened && (
        <Button theme="fill_primary" size={size} onClick={handleButtonClick} fullWidth>
          {buttonContent as any}
        </Button>
      )}
    </div>
  );
};
